import { Stack, Text } from '@mantine/core';
import { useEffect, useRef } from 'react';

import { useT } from '../i18n';
import { adminApi } from '../lib/api';

/** The embedded LibSQL/Outerbase Studio client (open source). */
const EMBED_URL = 'https://libsqlstudio.com/embed/sqlite?name=Prepzio%20D1';
/**
 * Replies are addressed to the studio's origin, never `*`.
 *
 * What travels back through this bridge is the result of an admin SQL query —
 * rows out of the production database. `*` delivers that to whatever origin the
 * frame happens to hold at the time: checking `e.source` proves who ASKED, and
 * nothing about who will receive the answer if the frame has navigated since.
 */
const EMBED_ORIGIN = new URL(EMBED_URL).origin;

type StudioRequest =
  | { type: 'query'; id: number; statement: string }
  | { type: 'transaction'; id: number; statements: string[] };

/**
 * Raw SQL console. Embeds the open-source LibSQL/Outerbase Studio client and
 * bridges its postMessage protocol to our admin SQL endpoint:
 *   iframe → { type:'query'|'transaction', id, statement/statements }
 *   parent  → { type, id, data } | { type, id, error }
 *
 * The studio never sees the session token — the parent (this component) holds
 * it and calls the authed `/v1/admin/db/query` endpoint. Full read/write incl.
 * DDL; admin-gated API-side.
 */
export function DatabaseScreen() {
  const ref = useRef<HTMLIFrameElement>(null);
  const t = useT();
  // The bridge is registered once and outlives a language switch, so the error
  // string is read through a ref rather than captured in the listener closure.
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const msg = e.data as StudioRequest | undefined;
      if (!msg || (msg.type !== 'query' && msg.type !== 'transaction')) return;
      const win = ref.current?.contentWindow;
      // Only answer our own iframe.
      if (!win || e.source !== win) return;

      const reply = (payload: { data?: unknown; error?: string }) =>
        win.postMessage({ type: msg.type, id: msg.id, ...payload }, EMBED_ORIGIN);

      const run =
        msg.type === 'query'
          ? adminApi.dbQuery({ statement: msg.statement })
          : adminApi.dbQuery({ statements: msg.statements });

      run
        .then((data) => reply({ data }))
        .catch((err: unknown) =>
          reply({
            error: err instanceof Error ? err.message : tRef.current('database.queryFailed'),
          }),
        );
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t('database.intro')}
      </Text>
      <iframe
        ref={ref}
        title={t('database.frameTitle')}
        src={EMBED_URL}
        style={{
          width: '100%',
          height: '72vh',
          border: '1px solid var(--mantine-color-default-border)',
          borderRadius: 8,
        }}
      />
    </Stack>
  );
}
